import * as angular from 'angular';
import { gpCommon } from '../app';
import { arrayRemoveValue } from '#gp/app-common/util/arrays';
import { modalKeyboardAvoidance } from './modal-keyboard-avoidance';

export enum EnterKeyBehaviour {
    CloseOnEnter,
    CloseOnCtrlEnter,
    DoNothing,
}

// this is mostly a typed interface into $modal, but with some extra properties
export interface IUibModalDialogOptions {
    animation?: boolean;
    templateUrl?: string;
    // $uibModal accepts markup directly as well as a URL. Callers that import
    // their template through webpack pass it here, so it ships in the bundle
    // instead of being fetched from an unversioned URL at runtime.
    template?: string;
    controller?: string;
    backdrop?: boolean | 'static';
    backdropClass?: string;
    windowClass?: string;
    resolve?: any;
    bindToController?: boolean;
    controllerAs?: string;
    component?: string;

    // This is our own extra property to help control what happens with the enter key
    // being pressed and how it relates to dialogs.
    enterKeyBehaviour?: EnterKeyBehaviour;
}

interface IUibModal {
    open<TDialogResult>(dialogOptions: IUibModalDialogOptions): IUibModalInstance<TDialogResult>;
}

export interface IUibModalInstance<T> {
    close(result: T): void;
    dismiss(reason: string): void;
    opened: ng.IPromise<any>;
    rendered: ng.IPromise<any>;
    result: ng.IPromise<T>;
}

class ModalReference {
    public cssClass: string;
    public cssSelector: string;

    scope(): ng.IScope {
        return angular.element(this.cssSelector).scope();
    }

    constructor(private id: string) {
        this.cssClass = "gp-class-" + id;
        this.cssSelector = "." + this.cssClass;
    }
}

export class ModalService {
    static $inject = ['$uibModal', '$rootScope'];
    private dialogCount = 0;
    private dialogStack: string[] = [];

    private dialogEnterKeyBehaviours = new Map<string, EnterKeyBehaviour>();

    constructor(private readonly $uibModal: IUibModal, private readonly $rootScope: ng.IRootScopeService) {
        $(document).on("keydown", ev => {
            const dialogId = this.dialogStack.at(-1);

            if (ev.key === "Enter" && dialogId) {
                let shouldCloseDialog = false;

                let enterKeyBehaviour = this.dialogEnterKeyBehaviours.get(dialogId);

                if (enterKeyBehaviour === undefined) {
                    enterKeyBehaviour = EnterKeyBehaviour.CloseOnCtrlEnter
                }

                if (enterKeyBehaviour === EnterKeyBehaviour.CloseOnEnter) {
                    shouldCloseDialog = true;
                } else if (enterKeyBehaviour === EnterKeyBehaviour.CloseOnCtrlEnter && ev.ctrlKey) {
                    shouldCloseDialog = true;
                }
                if (shouldCloseDialog) {
                    let dlg = new ModalReference(dialogId);
                    let scope: any = dlg.scope();
                    if (scope && scope.ok) {
                        ev.preventDefault();
                        try {
                            scope.ok();
                        } catch (ex) {
                            console.log(ex);
                        }
                    }
                }
            }
        });
    }

    open<TDialogResult>(dialogOptions: IUibModalDialogOptions): IUibModalInstance<TDialogResult> {
        this.dialogCount++;
        const newDialogId = "gpModal" + this.dialogCount;

        const uniqueDialogClass = "gp-class-" + newDialogId;

        if (dialogOptions.windowClass) {
            dialogOptions.windowClass += " " + uniqueDialogClass;
        } else {
            dialogOptions.windowClass = uniqueDialogClass;
        }

        this.dialogEnterKeyBehaviours.set(newDialogId, dialogOptions.enterKeyBehaviour !== undefined ? dialogOptions.enterKeyBehaviour : EnterKeyBehaviour.CloseOnCtrlEnter);

        this.$rootScope.$broadcast('gp-dialog-opening');

        const instance = this.$uibModal.open<TDialogResult>(dialogOptions);

        // Balanced against modalClosed below; a dialog that never opens
        // (opened rejects) must not leave the avoidance manager counting it.
        let keyboardAvoidanceCounted = false;

        instance.opened.then(() => {
            // First, before the broadcast: a throwing gp-dialog-opened
            // subscriber would otherwise kill this line and the catch below
            // would swallow the evidence.
            keyboardAvoidanceCounted = true;
            modalKeyboardAvoidance.modalOpened();

            this.dialogStack.push(newDialogId);
            this.$rootScope.$broadcast('gp-dialog-opened', { id: newDialogId, instance: instance });
        }).catch(err => { });

        instance.rendered.then(() => {
        })

        instance.result.finally(() => {
            this.$rootScope.$broadcast('gp-dialog-closed');

            if (keyboardAvoidanceCounted) {
                modalKeyboardAvoidance.modalClosed();
            }

            // a pop should work, but just in case something goes screwy with order, 
            // use the id just in case.
            arrayRemoveValue(this.dialogStack, newDialogId);

            this.dialogEnterKeyBehaviours.delete(newDialogId);
        });

        return instance;
    }
}

gpCommon.service("gpModal", ModalService);

